import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Form, Row, Col, Button, Alert } from 'react-bootstrap';
import { FaHeart, FaRegCalendarAlt, FaUserFriends, FaChild, FaExclamationTriangle, FaBus, FaMusic, FaPaperPlane, FaCheckCircle } from 'react-icons/fa';
import { useAxiosGet, useAxiosPost } from '../../../api/apiHook';
import '../WeddingPage.css';
import './RsvpPage.css';

function formatDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('es-ES', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
}

const EMPTY_FORM = {
  will_attend: true,
  adult_count: 1,
  adult_names: '',
  children_count: 0,
  allergies: '',
  needs_bus: false,
  song_request: '',
};

export const RsvpPage = () => {
  const { domain } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const { data: card, error: cardError, loaded: cardLoaded } = useAxiosGet(
    'cards/findByDomain/',
    { domain },
  );

  const { post, loading: submitting, error: submitError } = useAxiosPost(
    'cards/createAttendance/',
    { token },
  );

  const [form, setForm] = useState(EMPTY_FORM);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await post({
        ...form,
        adult_count: Number(form.adult_count) || 0,
        children_count: Number(form.children_count) || 0,
      });
      setSubmitted(true);
      setForm(EMPTY_FORM);
    } catch {
      // el error ya queda expuesto vía submitError
    }
  };

  const s = card?.wedding_settings || {};
  const vars = {
    '--wp-bg': s.backgroundColor || '#FAFAF8',
    '--wp-accent': s.buttonsColor || '#9baf8a',
    '--wp-text': s.descriptionTextColor || '#6A3B2E',
    '--wp-text-light': s.buttonsTextColor || '#FFFFFF',
    '--wp-border': s.buttonsBorderColor || s.backgroundColor || '#d8d8d6',
  };

  if (!token) {
    return (
      <div className="wp-page" style={vars}>
        <div className="wp-card rsvp-card">
          <Alert variant="danger">Este enlace no es válido. Pide a los novios el enlace de confirmación de asistencia.</Alert>
        </div>
      </div>
    );
  }

  if (!cardLoaded) {
    return <div className="wp-page" style={vars}><div className="wp-card rsvp-card" /></div>;
  }

  if (cardError) {
    return (
      <div className="wp-page" style={vars}>
        <div className="wp-card rsvp-card">
          <Alert variant="danger">No hemos podido cargar esta boda.</Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="wp-page" style={vars}>
      <div className="wp-card rsvp-card">
        <header className="wp-header">
          <p className="wp-subtitle">Confirmar asistencia</p>
          <h1 className="wp-couple rsvp-couple">
            {card?.bride_name || ''}<span className="wp-amp"> &amp; </span>{card?.groom_name || ''}
          </h1>
          {card?.wedding_date && (
            <p className="wp-date-line">
              <FaRegCalendarAlt /> {formatDate(card.wedding_date)}
            </p>
          )}
        </header>

        <hr className="wp-hr" />

        {submitted && (
          <Alert variant="success" className="rsvp-alert">
            <FaCheckCircle className="rsvp-alert-icon" /> ¡Gracias! Tu confirmación se ha registrado correctamente.
          </Alert>
        )}

        {submitError && (
          <Alert variant="danger" className="rsvp-alert">
            No hemos podido registrar tu confirmación. Revisa el enlace e inténtalo de nuevo.
          </Alert>
        )}

        <Form onSubmit={handleSubmit} className="rsvp-form">
          <Form.Check
            type="switch"
            id="will_attend"
            className="rsvp-switch"
            label={form.will_attend ? '¡Allí estaré!' : 'No podré asistir'}
            checked={form.will_attend}
            onChange={handleChange('will_attend')}
          />

          {form.will_attend && (
            <>
              <Form.Group className="rsvp-field">
                <Form.Label><FaUserFriends /> Nombre y apellidos de los adultos</Form.Label>
                <Form.Control
                  type="text"
                  value={form.adult_names}
                  onChange={handleChange('adult_names')}
                  placeholder="Ej: Ana Pérez, Luis Gómez"
                />
              </Form.Group>

              <Row>
                <Col xs={6}>
                  <Form.Group className="rsvp-field">
                    <Form.Label><FaUserFriends /> Adultos</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      value={form.adult_count}
                      onChange={handleChange('adult_count')}
                    />
                  </Form.Group>
                </Col>
                <Col xs={6}>
                  <Form.Group className="rsvp-field">
                    <Form.Label><FaChild /> Niños</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      value={form.children_count}
                      onChange={handleChange('children_count')}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="rsvp-field">
                <Form.Label><FaExclamationTriangle /> Alergias / intolerancias</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={form.allergies}
                  onChange={handleChange('allergies')}
                  placeholder="Cuéntanos si hay algo que debamos tener en cuenta"
                />
              </Form.Group>

              <Form.Group className="rsvp-field">
                <Form.Label><FaMusic /> Petición musical</Form.Label>
                <Form.Control
                  type="text"
                  value={form.song_request}
                  onChange={handleChange('song_request')}
                  placeholder="Una canción que no puede faltar"
                />
              </Form.Group>

              <Form.Check
                type="checkbox"
                id="needs_bus"
                className="rsvp-checkbox"
                label={<><FaBus /> Voy a hacer uso del servicio de autobús</>}
                checked={form.needs_bus}
                onChange={handleChange('needs_bus')}
              />
            </>
          )}

          {!form.will_attend && (
            <Form.Group className="rsvp-field">
              <Form.Label><FaUserFriends /> Nombre y apellidos</Form.Label>
              <Form.Control
                type="text"
                required
                value={form.adult_names}
                onChange={handleChange('adult_names')}
                placeholder="Ej: Ana Pérez"
              />
            </Form.Group>
          )}

          <Button type="submit" disabled={submitting} className="wp-btn rsvp-submit">
            {submitting ? 'Enviando...' : <><FaPaperPlane /> Confirmar asistencia</>}
          </Button>
        </Form>

        <hr className="wp-hr" />
        <FaHeart className="wp-icon-heart rsvp-closing-heart" />
      </div>
    </div>
  );
};
